/** PHP (string) after JSON decoding, with precision=14 and a 64-bit integer build.
 * This models the JSON boundary: native PHP int/float distinctions erased by JSON
 * cannot be inferred from a JavaScript number. Reject non-JSON numeric inputs.
 */
export function phpJsonNumberString(value: number): string {
    if (!Number.isFinite(value)) throw new TypeError('PHP number string conversion requires finite JSON numbers');
    const encoded = JSON.stringify(value);
    if (/^-?\d+$/.test(encoded)) {
        const integer = BigInt(encoded);
        if (integer >= -9223372036854775808n && integer <= 9223372036854775807n) return encoded;
    }
    const magnitude = Math.abs(value);
    if (magnitude === 0) return '0';
    const bits = new DataView(new ArrayBuffer(8));
    bits.setFloat64(0, magnitude);
    const word = bits.getBigUint64(0), exponentBits = Number((word >> 52n) & 2047n);
    const fraction = word & ((1n << 52n) - 1n);
    let numerator = exponentBits === 0 ? fraction : fraction + (1n << 52n);
    let denominator = 1n;
    const binaryExponent = (exponentBits === 0 ? -1022 : exponentBits - 1023) - 52;
    if (binaryExponent >= 0) numerator <<= BigInt(binaryExponent); else denominator <<= BigInt(-binaryExponent);
    const ten = (exponent: number): bigint => 10n ** BigInt(exponent);
    const atLeastPower = (exponent: number): boolean => exponent >= 0
        ? numerator >= denominator * ten(exponent) : numerator * ten(-exponent) >= denominator;
    let exponent = Math.floor(Math.log10(magnitude));
    while (!atLeastPower(exponent)) exponent--;
    while (atLeastPower(exponent + 1)) exponent++;
    const scale = 13 - exponent;
    if (scale >= 0) numerator *= ten(scale); else denominator *= ten(-scale);
    let rounded = numerator / denominator;
    const twiceRemainder = (numerator % denominator) * 2n;
    // zend_dtoa significant-digit rounding: nearest, with an even last digit on ties.
    if (twiceRemainder > denominator || (twiceRemainder === denominator && rounded % 2n !== 0n)) rounded++;
    if (rounded >= 100000000000000n) { rounded /= 10n; exponent++; }
    const digits = String(rounded).replace(/0+$/, ''), sign = value < 0 ? '-' : '';
    if (exponent < -4 || exponent >= 14) {
        return `${sign}${digits[0]}.${digits.slice(1) || '0'}E${exponent >= 0 ? '+' : ''}${exponent}`;
    }
    const point = exponent + 1;
    return sign + (point <= 0 ? '0.' + '0'.repeat(-point) + digits
        : point >= digits.length ? digits + '0'.repeat(point - digits.length)
            : digits.slice(0, point) + '.' + digits.slice(point));
}
